// Monthly holdings history: per-client, per-month snapshots used for
// net-of-deposits return (Modified Dietz) and time-travel ("see any past month").
//
// Stored under pension_holdings_history_v1 (registered in storage.js STORAGE_KEYS):
// { [clientId]: { snapshots: [ snapshot ] } }  — snapshots sorted by period asc.
//
// snapshot = {
//   period: "YYYY-MM", valueDate: "YYYY-MM-DD", importedAt,
//   totalBalance, totalDeposits, totalWithdrawals, ret,           // aggregate (portfolio)
//   perFund: [ { key, companyName, sugMutzar, polNum,
//                balance, deposits, withdrawals, ret,
//                dmeiNihulTzv, dmeiNihulHaf,
//                maslulim: [{name, balance, tsua, trackId}],
//                flows: [{date, amount(signed)}] } ]               // interval flows (compact)
// }

import { memoryStore } from "./storage";
import { computePeriodReturn, flowsInInterval, sumFlows, sumFlowsByType } from "./monthlyReturn";

const KEY = "pension_holdings_history_v1";
export const HISTORY_EVENT = "pension-holdings-history-updated";

function read() {
  try {
    const raw = memoryStore.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function write(obj) {
  try {
    memoryStore.setItem(KEY, JSON.stringify(obj));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(HISTORY_EVENT));
    }
  } catch (e) {
    console.error("Failed to persist holdings history", e);
  }
}

function fundKey(f) {
  return f.id || `${f.companyCode || ""}_${f.polNum || ""}_${f.kidodAchid || ""}`;
}

function fundBalance(f) {
  return f.yitrot?.totalBalance || f.redeem || 0;
}

// A fund counts as employer-backed if it has an employer entity or any employer-side
// annual deposits. Used to label the insured-person bucket as "עובד" vs "עצמאי".
function hasEmployerFor(f) {
  return !!(f.employer?.id) || (f.annualDeposits?.maavid > 0);
}

// fund.yitrot.date is "DD/MM/YYYY" (fmtDate). Convert to ISO + period.
function valueDateOf(funds) {
  let best = null; // ISO
  for (const f of funds) {
    const d = f.yitrot?.date;
    if (!d) continue;
    const m = String(d).match(/(\d{2})\/(\d{2})\/(\d{4})/);
    const iso = m ? `${m[3]}-${m[2]}-${m[1]}` : null;
    if (iso && (!best || iso > best)) best = iso;
  }
  return best;
}

// Compute returns for snapshot `cur` against snapshot `prev` (mutates cur in place).
function computeReturns(cur, prev) {
  if (!prev) {
    cur.ret = null;
    for (const pf of cur.perFund) pf.ret = null;
    return;
  }
  const prevByKey = new Map(prev.perFund.map((pf) => [pf.key, pf]));
  for (const pf of cur.perFund) {
    const p = prevByKey.get(pf.key);
    pf.ret = p
      ? computePeriodReturn({
          V0: p.balance,
          V1: pf.balance,
          flows: pf.flows,
          periodStart: prev.valueDate,
          periodEnd: cur.valueDate,
        })
      : null;
  }
  // Aggregate (portfolio) return
  const allFlows = cur.perFund.flatMap((pf) => pf.flows);
  cur.ret = computePeriodReturn({
    V0: prev.totalBalance,
    V1: cur.totalBalance,
    flows: allFlows,
    periodStart: prev.valueDate,
    periodEnd: cur.valueDate,
  });
}

// Record/replace the monthly snapshot for a client from their merged holdings funds.
// `funds` = ALL of the client's funds across companies for the current import.
export function recordSnapshot(clientId, funds, fileMeta = {}) {
  if (!clientId || !funds?.length) return;
  let valueDate = null;
  if (fileMeta.taarichBitzua) {
    const m = String(fileMeta.taarichBitzua).match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (m) valueDate = `${m[3]}-${m[2]}-${m[1]}`;
  }
  if (!valueDate) valueDate = valueDateOf(funds) || (fileMeta.valueDate || null);
  if (!valueDate) return; // can't place it in time
  const period = valueDate.slice(0, 7);

  const all = read();
  const entry = all[clientId] || { snapshots: [] };
  // chronological predecessor (greatest period strictly before this one)
  const prev = entry.snapshots
    .filter((s) => s.period < period)
    .sort((a, b) => (a.period < b.period ? 1 : -1))[0] || null;
  const prevValueDate = prev?.valueDate || "0000-00-00";

  // de-dupe funds by key (a client may have the same policy from multiple ZIPs)
  const seen = new Set();
  const perFund = [];
  let totalBalance = 0;
  let totalDeposits = 0;
  let totalWithdrawals = 0;
  let totalEmployer = 0;
  let totalInsured = 0;
  let totalOneTime = 0;
  for (const f of funds) {
    const key = fundKey(f);
    if (seen.has(key)) continue;
    seen.add(key);
    const balance = fundBalance(f);
    const flows = flowsInInterval(f.flows, prevValueDate, valueDate);
    const { deposits, withdrawals } = sumFlows(f.flows, prevValueDate, valueDate);
    const breakdown = sumFlowsByType(f.flows, prevValueDate, valueDate, { hasEmployer: hasEmployerFor(f) });
    totalBalance += balance;
    totalDeposits += deposits;
    totalWithdrawals += withdrawals;
    totalEmployer += breakdown.employer;
    totalInsured += breakdown.insured;
    totalOneTime += breakdown.oneTime;
    perFund.push({
      ...f,
      key,
      companyName: f.companyName,
      sugMutzar: f.sugMutzar,
      polNum: f.polNum,
      balance,
      deposits,
      withdrawals,
      depositsBreakdown: breakdown,
      ret: null,
      dmeiNihulTzv: f.dmeiNihul?.actual?.dmeiTzvPct ?? null,
      dmeiNihulHaf: f.dmeiNihul?.actual?.dmeiHafPct ?? null,
      maslulim: (f.maslulim || []).map((m) => ({ name: m.name, balance: m.balance, tsua: m.tsua, trackId: m.trackId })),
      flows,
    });
  }

  const snapshot = {
    period,
    valueDate,
    importedAt: new Date().toISOString(),
    totalBalance,
    totalDeposits,
    totalWithdrawals,
    totalEmployer,
    totalInsured,
    totalOneTime,
    ret: null,
    perFund,
  };

  computeReturns(snapshot, prev);

  // upsert by period
  const others = entry.snapshots.filter((s) => s.period !== period);
  const next = [...others, snapshot].sort((a, b) => (a.period < b.period ? -1 : 1));

  // recompute the snapshot that now follows the inserted/replaced one
  const idx = next.findIndex((s) => s.period === period);
  if (idx >= 0 && idx + 1 < next.length) {
    computeReturns(next[idx + 1], next[idx]);
  }

  all[clientId] = { snapshots: next };
  write(all);
}

export function recordSnapshotsBatch(updates) {
  if (!updates || !updates.length) return;
  const all = read();
  let changed = false;

  for (const { clientId, funds, fileMeta = {} } of updates) {
    if (!clientId || !funds?.length) continue;
    let valueDate = null;
    if (fileMeta.taarichBitzua) {
      const m = String(fileMeta.taarichBitzua).match(/(\d{2})\/(\d{2})\/(\d{4})/);
      if (m) valueDate = `${m[3]}-${m[2]}-${m[1]}`;
    }
    if (!valueDate) valueDate = valueDateOf(funds) || (fileMeta.valueDate || null);
    if (!valueDate) continue; 
    const period = valueDate.slice(0, 7);

    const entry = all[clientId] || { snapshots: [] };
    const prev = entry.snapshots
      .filter((s) => s.period < period)
      .sort((a, b) => (a.period < b.period ? 1 : -1))[0] || null;
    const prevValueDate = prev?.valueDate || "0000-00-00";

    const seen = new Set();
    const perFund = [];
    let totalBalance = 0;
    let totalDeposits = 0;
    let totalWithdrawals = 0;
    let totalEmployer = 0;
    let totalInsured = 0;
    let totalOneTime = 0;

    for (const f of funds) {
      const key = fundKey(f);
      if (seen.has(key)) continue;
      seen.add(key);
      const balance = fundBalance(f);
      const flows = flowsInInterval(f.flows, prevValueDate, valueDate);
      const { deposits, withdrawals } = sumFlows(f.flows, prevValueDate, valueDate);
      const breakdown = sumFlowsByType(f.flows, prevValueDate, valueDate, { hasEmployer: hasEmployerFor(f) });
      totalBalance += balance;
      totalDeposits += deposits;
      totalWithdrawals += withdrawals;
      totalEmployer += breakdown.employer;
      totalInsured += breakdown.insured;
      totalOneTime += breakdown.oneTime;
      perFund.push({
        ...f,
        key, companyName: f.companyName, sugMutzar: f.sugMutzar, polNum: f.polNum, balance, deposits, withdrawals,
        depositsBreakdown: breakdown,
        ret: null, dmeiNihulTzv: f.dmeiNihul?.actual?.dmeiTzvPct ?? null, dmeiNihulHaf: f.dmeiNihul?.actual?.dmeiHafPct ?? null,
        maslulim: (f.maslulim || []).map((m) => ({ name: m.name, balance: m.balance, tsua: m.tsua, trackId: m.trackId })),
        flows,
      });
    }

    const snapshot = {
      period, valueDate, importedAt: new Date().toISOString(), totalBalance, totalDeposits, totalWithdrawals,
      totalEmployer, totalInsured, totalOneTime, ret: null, perFund,
    };
    computeReturns(snapshot, prev);
    
    const others = entry.snapshots.filter((s) => s.period !== period);
    const next = [...others, snapshot].sort((a, b) => (a.period < b.period ? -1 : 1));
    const idx = next.findIndex((s) => s.period === period);
    if (idx >= 0 && idx + 1 < next.length) {
      computeReturns(next[idx + 1], next[idx]);
    }
    all[clientId] = { snapshots: next };
    changed = true;
  }
  
  if (changed) write(all);
}

// ── selectors ────────────────────────────────────────────────────────────────
export function getClientHistory(clientId) {
  return read()[clientId]?.snapshots || [];
}
export function listPeriods(clientId) {
  return getClientHistory(clientId).map((s) => s.period);
}
export function getSnapshot(clientId, period) {
  return getClientHistory(clientId).find((s) => s.period === period) || null;
}
export function removeClientHistory(clientId) {
  const all = read();
  if (all[clientId]) {
    delete all[clientId];
    write(all);
  }
}
export function removeClientHistoriesBatch(clientIds) {
  if (!clientIds?.length) return;
  const all = read();
  let changed = false;
  for (const cid of clientIds) {
    if (all[cid]) {
      delete all[cid];
      changed = true;
    }
  }
  if (changed) write(all);
}

// ניקוי חודשים שגויים: מסיר snapshots שהחודש שלהם מאוחר מתאריך הנכונות האמיתי
// של הלקוח (cutoffByClient[clientId] = "YYYY-MM"). חודש מאוחר מהנתונים = ארטיפקט
// של תאריך הפקה/תיבה (ההפקה תמיד ≥ תאריך הנתונים). לקוח ללא cutoff מדלגים עליו.
// מחזיר: [{ clientId, periods: ["YYYY-MM", ...] }] של מה שהוסר.
export function pruneMonthsAfter(cutoffByClient) {
  const all = read();
  let changed = false;
  const removed = [];
  for (const [cid, cutoff] of Object.entries(cutoffByClient || {})) {
    if (!cutoff) continue;
    const entry = all[cid];
    if (!entry?.snapshots?.length) continue;
    const dropped = entry.snapshots.filter((s) => s.period > cutoff).map((s) => s.period);
    if (!dropped.length) continue;
    entry.snapshots = entry.snapshots.filter((s) => s.period <= cutoff);
    if (!entry.snapshots.length) delete all[cid];
    removed.push({ clientId: cid, periods: dropped });
    changed = true;
  }
  if (changed) write(all);
  return removed;
}
